import { useState, useEffect } from 'react';
import { departmentsApi, Department } from '../../api/departments.api';
import Table, { Column } from '../../components/common/Table';
import Button from '../../components/forms/Button';
import Loading from '../../components/common/Loading';
import Modal from '../../components/common/Modal';
import Input from '../../components/forms/Input';

const DepartmentList = () => {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState<Department | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      setIsLoading(true);
      const data = await departmentsApi.getAll();
      setDepartments(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Departmanlar yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (department?: Department) => {
    if (department) {
      setEditingDepartment(department);
      setFormData({
        name: department.name,
        description: department.description || '',
      });
    } else {
      setEditingDepartment(null);
      setFormData({
        name: '',
        description: '',
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingDepartment(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingDepartment) {
        await departmentsApi.update(editingDepartment.id, formData);
      } else {
        await departmentsApi.create(formData);
      }
      await fetchDepartments();
      handleCloseModal();
    } catch (err: any) {
      setError(err.message || 'İşlem başarısız oldu');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Bu departmanı silmek istediğinizden emin misiniz?')) {
      return;
    }
    try {
      await departmentsApi.delete(id);
      await fetchDepartments();
    } catch (err: any) {
      setError(err.message || 'Silme işlemi başarısız oldu');
    }
  };

  const columns: Column<Department>[] = [
    { key: 'name', header: 'Departman Adı' },
    { key: 'description', header: 'Açıklama', render: (item) => item.description || '-' },
    {
      key: 'isActive',
      header: 'Durum',
      render: (item) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-semibold ${
            item.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}
        >
          {item.isActive ? 'Aktif' : 'Pasif'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'İşlemler',
      render: (item) => (
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" onClick={() => handleOpenModal(item)}>
            Düzenle
          </Button>
          <Button size="sm" variant="danger" onClick={() => handleDelete(item.id)}>
            Sil
          </Button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <Loading text="Departmanlar yükleniyor..." />;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Departmanlar</h1>
        <Button onClick={() => handleOpenModal()}>Yeni Departman Ekle</Button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
          {error}
        </div>
      )}

      <Table data={departments} columns={columns} emptyMessage="Henüz departman bulunmamaktadır" />

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingDepartment ? 'Departman Düzenle' : 'Yeni Departman Ekle'}
      >
        <form onSubmit={handleSubmit}>
          <Input
            label="Departman Adı"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <Input
            label="Açıklama"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
          <div className="flex gap-2 mt-6">
            <Button type="submit" fullWidth>
              {editingDepartment ? 'Güncelle' : 'Ekle'}
            </Button>
            <Button type="button" variant="outline" fullWidth onClick={handleCloseModal}>
              İptal
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default DepartmentList;
